// Add from Hugging Face (acceptance E1–E8, F1–F7): look the repo up, ask for a
// token when it's gated, then pick the GPU and download the weights to the
// tenant's S3 library once.
import { useState } from "react";
import { authApi, modelServingApi } from "../../api/client";
import { COLORS } from "../../theme/colors";
import {
  accessBadge, archText, buildAddBody, fitBlockText, fitHint, fmtGB, formatText, gpuOptionLabel,
  paramsText, parseRepo, recommendationText, shortSha, timeAgo,
} from "./modelServingModel";
import { Badge, Btn, Dialog, Field, MONO, Notice, errorText, input } from "./modelServingUi";

const PILL = (on) => ({
  padding: "4px 11px", borderRadius: 999, fontSize: 12, fontWeight: 600,
  color: on ? COLORS.accentText : COLORS.textDim, background: on ? COLORS.accentSoft : "transparent",
  border: `1px solid ${on ? COLORS.accent : COLORS.border}`,
});

function formFrom(found) {
  const rec = found.recommendation || {};
  const opts = found.gpu_options || [];
  return {
    display_name: found.suggested_name || "", key: found.suggested_key || "",
    node_class: (rec.fits && rec.node_class) || (opts.find((o) => o.fits) || opts[0] || {}).key || "",
    image: found.suggested_image || "", max_model_len: "",
    extra_args: (found.suggested_extra_args || []).join(" "),
  };
}

function Resolved({ found }) {
  const mdl = found.model;
  const facts = [
    ["Parameters", paramsText(mdl)], ["Format", formatText(mdl)],
    ["Download size", `${fmtGB(mdl.bytes_total)} · ${mdl.files_total || 0} files`],
    ["Architecture", archText(mdl)], ["Version", `${shortSha(mdl.sha)} · ${timeAgo(mdl.last_modified)}`],
    ["License", mdl.license || "—"],
  ];
  const rec = found.recommendation;
  return (
    <>
      <div data-lc="repo-metadata" style={{ border: `1px solid ${COLORS.border}`, borderRadius: 14, padding: 14, marginBottom: 12 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
          <b style={{ wordBreak: "break-all" }}>{mdl.repo.split("/").pop()}</b>
          <Badge label={accessBadge(mdl)} tone={accessBadge(mdl) === "Public" ? "green" : "amber"} />
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
          {facts.map(([k, v]) => (
            <div key={k}>
              <div style={{ fontSize: 10.5, textTransform: "uppercase", letterSpacing: 0.5, color: COLORS.textDim }}>{k}</div>
              <div style={{ fontSize: 13, fontFamily: k === "Version" ? MONO : undefined }}>{v}</div>
            </div>
          ))}
        </div>
      </div>
      {rec && (
        <Notice lc="gpu-recommendation" tone={rec.fits ? "ok" : "bad"} icon={rec.fits ? "✓" : "⛔"}>
          {recommendationText(rec)}
        </Notice>
      )}
    </>
  );
}

export default function AddModelWizard({ onClose, onAdded }) {
  const [step, setStep] = useState(1);
  const [repoIn, setRepoIn] = useState("");
  const [busy, setBusy] = useState(false);
  const [found, setFound] = useState(null);
  const [err, setErr] = useState(null);
  const [gated, setGated] = useState(null);       // {repo} when the repo needs a token
  const [token, setToken] = useState("");
  const [form, setForm] = useState(null);

  const lookup = async () => {
    const repo = parseRepo(repoIn);
    setErr(null); setFound(null);
    if (!repo) {
      setErr("Enter a Hugging Face repo id like owner/name, or paste its huggingface.co link.");
      return;
    }
    setBusy(true);
    try {
      const r = await modelServingApi.lookup(repo);
      setFound(r.data); setForm(formFrom(r.data)); setGated(null);
    } catch (e) {
      if (e?.configRequired) {
        if (gated) setErr(`Hugging Face still refused access: ${e.configRequired.message}`);
        setGated({ repo });
      } else {
        setErr(errorText(e));
      }
    } finally {
      setBusy(false);
    }
  };

  const saveTokenAndLookup = async () => {
    setBusy(true); setErr(null);
    try {
      await authApi.updateHuggingFaceConfig({ huggingface_token: token.trim() });
    } catch (e) {
      setErr(errorText(e)); setBusy(false);
      return;
    }
    await lookup();
  };

  const download = async () => {
    setBusy(true); setErr(null);
    try {
      const r = await modelServingApi.add(buildAddBody({
        ...form, repo: found.model.repo, revision: found.model.sha || "main",
      }));
      onAdded?.(r.data);
      onClose();
    } catch (e) {
      setErr(errorText(e));
      setBusy(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const opts = found?.gpu_options || [];
  const opt = opts.find((o) => o.key === form?.node_class);
  const blocked = step === 2 && opt && !opt.fits;

  const footer = (
    <>
      <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
      {step === 2 && <Btn onClick={() => setStep(1)}>Back</Btn>}
      {step === 1 && gated && !found && (
        <Btn kind="primary" disabled={busy || !token.trim()} onClick={saveTokenAndLookup}>Save token &amp; look up again</Btn>
      )}
      {step === 1 && !(gated && !found) && (
        <Btn kind="primary" disabled={!found || busy} onClick={() => setStep(2)}>Continue</Btn>
      )}
      {step === 2 && (
        <Btn kind="primary" data-lc="start-download" disabled={busy || blocked || !form.node_class}
          onClick={download}>Download &amp; add to library</Btn>
      )}
    </>
  );

  return (
    <Dialog testid="add-model-dialog" title="Add from Hugging Face" glyph="＋" maxWidth={620} onClose={onClose}
      sub="We copy the weights into your S3 library once. After that, every warm-up loads from S3." footer={footer}>
      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap" }}>
        <span style={PILL(step === 1)}>1 · Find the model</span>
        <span style={PILL(step === 2)}>2 · Configure &amp; download</span>
      </div>

      {step === 1 && (
        <>
          <Field label="Hugging Face repo" htmlFor="ms-repo"
            hint="Paste a repo id or its huggingface.co URL. We pin the exact version (commit) you add.">
            <div style={{ display: "flex", gap: 8 }}>
              <input id="ms-repo" style={input()} value={repoIn} placeholder="nvidia/NVIDIA-Nemotron-3-Super-120B-A12B-FP8"
                onChange={(e) => setRepoIn(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") lookup(); }} />
              <Btn data-lc="lookup" disabled={busy} onClick={lookup}>{busy ? "Looking up…" : "Look up"}</Btn>
            </div>
          </Field>
          {gated && !found && (
            <Notice lc="credential-prompt" tone="warn" icon="🔒">
              <b style={{ color: COLORS.text }}>{gated.repo} is a gated repo.</b> Hugging Face needs a token from an
              account that has accepted this model&apos;s license. The token is saved for your workspace only and used
              just to download weights.
              <div style={{ marginTop: 10 }}>
                <Field label="Hugging Face access token" htmlFor="ms-hf-token"
                  hint="Create a read token at huggingface.co → Settings → Access Tokens. You can change it later in Admin → Integrations → Hugging Face.">
                  <input id="ms-hf-token" type="password" autoComplete="off" style={input()} placeholder="hf_…"
                    value={token} onChange={(e) => setToken(e.target.value)} />
                </Field>
              </div>
            </Notice>
          )}
          {found && <Resolved found={found} />}
        </>
      )}

      {step === 2 && form && (
        <>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
            <Field label="Display name" htmlFor="ms-name">
              <input id="ms-name" style={input()} value={form.display_name} onChange={set("display_name")} />
            </Field>
            <Field label="Model key" htmlFor="ms-key" hint="Shown to agents as the model name.">
              <input id="ms-key" style={{ ...input(), fontFamily: MONO }} value={form.key} onChange={set("key")} />
            </Field>
          </div>
          <Field label="GPU" htmlFor="ms-gpu" hint={opt?.fits ? fitHint({ vram_required_gb: opt.vram_required_gb }, opt) : undefined}>
            <select id="ms-gpu" data-lc="gpu-choice-add" style={input()} value={form.node_class} onChange={set("node_class")}>
              {opts.map((o) => (
                <option key={o.key} value={o.key}>
                  {gpuOptionLabel(o, { recommended: found.recommendation?.fits ? found.recommendation.node_class : null })}
                </option>
              ))}
            </select>
          </Field>
          {blocked && (
            <Notice lc="gpu-fit-block" tone="bad" icon="⛔">
              <b style={{ color: COLORS.text }}>This GPU can&apos;t hold the model.</b> {fitBlockText(found.model, opt)}
            </Notice>
          )}
          <details data-lc="advanced-settings" style={{ marginBottom: 12 }}>
            <summary style={{ cursor: "pointer", fontSize: 12.5, color: COLORS.accentText, fontWeight: 600, marginBottom: 10 }}>
              Advanced — vLLM image, context length, extra arguments
            </summary>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12 }}>
              <Field label="vLLM image" htmlFor="ms-image">
                <input id="ms-image" style={input()} value={form.image} onChange={set("image")} />
              </Field>
              <Field label="Max context length" htmlFor="ms-ctx">
                <input id="ms-ctx" style={input()} inputMode="numeric" value={form.max_model_len}
                  placeholder="model default" onChange={set("max_model_len")} />
              </Field>
            </div>
            <Field label="Extra vLLM arguments" htmlFor="ms-args"
              hint="Pre-filled from the model's published serve settings. Change only if you know you need to.">
              <input id="ms-args" style={{ ...input(), fontFamily: MONO }} value={form.extra_args} onChange={set("extra_args")} />
            </Field>
          </details>
          <Notice tone="info" icon="⏳">
            <b style={{ color: COLORS.text }}>Downloads {fmtGB(found.model.bytes_total)} to your S3 library, once.</b> This runs
            in the background; you can close this window. The model becomes runnable when the download completes.
          </Notice>
        </>
      )}

      {err && <Notice testid="add-model-error" tone="bad" icon="!">{err}</Notice>}
    </Dialog>
  );
}
